import { ref, computed } from 'vue';
import { addCacheBuster, noStoreRequestInit } from '@/config/cache-flags';
import { useApplicationStore } from '@/store/application.store';

export interface AppVersion {
  version: string;
  buildTime: string;
  buildTimestamp: number;
  buildHash: string;
}

export class UpdateChecker {
  private currentVersion: AppVersion | null = null;
  private checkInterval: number | null = null;
  private readonly CHECK_INTERVAL_MS = 30000; // Check every 30 seconds
  private readonly VERSION_URL = '/version.json';
  private isCheckingForUpdates = ref(false);
  private updateAvailable = ref(false);
  private lastUpdateNotificationTime = 0;
  private readonly NOTIFICATION_COOLDOWN_MS = 300000; // 5 minutes cooldown

  constructor() {
    this.loadCurrentVersion();
  }

  private async loadCurrentVersion(): Promise<void> {
    try {
      const response = await fetch(addCacheBuster(this.VERSION_URL), noStoreRequestInit());
      if (response.ok) {
        this.currentVersion = await response.json();
        console.log('Current app version loaded:', this.currentVersion);
      } else {
        console.warn('Failed to load current version info');
      }
    } catch (error) {
      console.error('Error loading current version:', error);
    }
  }

  private async fetchLatestVersion(): Promise<AppVersion | null> {
    try {
      // Add timestamp to prevent caching if disabled
      const response = await fetch(addCacheBuster(this.VERSION_URL), noStoreRequestInit());
      
      if (response.ok) {
        return await response.json();
      } else {
        console.warn('Failed to fetch latest version');
        return null;
      }
    } catch (error) {
      console.error('Error fetching latest version:', error);
      return null;
    }
  }

  private isUpdateAvailable(current: AppVersion, latest: AppVersion): boolean {
    // Compare build timestamp first (most reliable)
    if (current.buildTimestamp !== latest.buildTimestamp) {
      return true;
    }
    
    // Compare build hash as secondary check
    if (current.buildHash !== latest.buildHash) {
      return true;
    }
    
    // Compare version as final check
    return current.version !== latest.version;
  }

  private async checkForUpdates(): Promise<void> {
    if (this.isCheckingForUpdates.value || !this.currentVersion) {
      return;
    }

    this.isCheckingForUpdates.value = true;

    try {
      const latestVersion = await this.fetchLatestVersion();
      
      if (latestVersion && this.isUpdateAvailable(this.currentVersion, latestVersion)) {
        console.log('Update available!', {
          current: this.currentVersion,
          latest: latestVersion
        });
        
        this.updateAvailable.value = true;
        this.showUpdateNotification();
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    } finally {
      this.isCheckingForUpdates.value = false;
    }
  }

  private showUpdateNotification(): void {
    const now = Date.now();
    
    // Respect cooldown period to avoid spam
    if (now - this.lastUpdateNotificationTime < this.NOTIFICATION_COOLDOWN_MS) {
      return;
    }

    this.lastUpdateNotificationTime = now;
    
    // Use enhanced banner system for a consistent UX
    try {
      const applicationStore = useApplicationStore();
      applicationStore.showBanner({
        id: 'app-update-available',
        content: '<strong>App update available.</strong> A new version is ready. Please refresh to get the latest features.',
        theme: 'warning',
        priority: 'high',
        position: 'top',
        sticky: true,
        dismissible: true,
        icon: 'exclamation-triangle',
        metadata: { dedupeKey: 'app-update-available' },
        actions: [
          {
            label: 'Refresh now',
            action: 'callback',
            callback: () => this.forceRefresh(),
            theme: 'primary'
          },
          {
            label: 'Dismiss',
            action: 'dismiss',
            theme: 'secondary'
          }
        ]
      });
    } catch (error) {
      console.warn('Failed to show update banner:', error);
    }
  }

  public forceRefresh(): void {
    try {
      // Clear all caches
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName);
          });
        });
      }

      // Clear localStorage (optional - be careful with user data)
      // localStorage.clear();

      // Force reload without cache
      window.location.reload();
    } catch (error) {
      console.error('Error during force refresh:', error);
      // Fallback to simple reload
      window.location.reload();
    }
  }

  public startUpdateChecking(): void {
    if (this.checkInterval) {
      return; // Already started
    }

    console.log('Starting update checking...');
    
    // Check immediately
    this.checkForUpdates();
    
    // Then check periodically
    this.checkInterval = window.setInterval(() => {
      this.checkForUpdates();
    }, this.CHECK_INTERVAL_MS);
  }

  public stopUpdateChecking(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log('Update checking stopped');
    }
  }

  public manualUpdateCheck(): Promise<void> {
    return this.checkForUpdates();
  }

  public dismissUpdate(): void {
    this.updateAvailable.value = false;
    this.lastUpdateNotificationTime = Date.now(); // Reset cooldown
  }

  // Reactive getters
  public get isChecking() {
    return computed(() => this.isCheckingForUpdates.value);
  }

  public get hasUpdateAvailable() {
    return computed(() => this.updateAvailable.value);
  }

  public get currentAppVersion() {
    return this.currentVersion;
  }
}

// Export singleton instance
export const updateChecker = new UpdateChecker();
export default updateChecker;